import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  labelById,
  parseAmountInput,
  startOfDay,
  toggleFilterListValue,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";

export { toggleFilterListValue };

export const EXCHANGE_CURRENCIES = [
  { id: "usd", label: "USD" },
  { id: "eur", label: "EUR" },
  { id: "pyg", label: "PYG" },
  { id: "brl", label: "BRL" },
];

export const EMPTY_EXCHANGE_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  fromCurrencies: [],
  toCurrencies: [],
  statuses: [],
  searchQuery: "",
};

export const getExchangeStatusOptions = () => [
  { id: "concluida", label: intl.get("contaInternacional.statusCompleted") },
  { id: "processando", label: intl.get("contaInternacional.statusProcessing") },
];

const parseExchangeDate = (dateStr) => {
  const [datePart, timePart = "00:00"] = dateStr.split(" ");
  const [day, month, year] = datePart.split("/").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

const normalizeStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("process")) return "processando";
  return "concluida";
};

const getExchangeAmount = (item) => {
  const toAmount = Number(item.amountTo);
  if (Number.isFinite(toAmount)) return Math.abs(toAmount);
  return Math.abs(Number(item.amountFrom) || 0);
};

export const getExchangeItems = (items = []) =>
  items.filter((item) => item.historyKind === "conversions");

export const hasActiveExchangeFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.fromCurrencies.length > 0
  || filters.toCurrencies.length > 0
  || filters.statuses.length > 0
  || Boolean(filters.searchQuery?.trim());

export const applyExchangeFilters = (items, filters) => {
  const minAmount = parseAmountInput(filters.amountMin);
  const maxAmount = parseAmountInput(filters.amountMax);
  const dateFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? endOfDay(filters.dateTo) : null;

  return items.filter((item) => {
    if (item.historyKind && item.historyKind !== "conversions") return false;

    const itemDate = parseExchangeDate(item.date);

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amount = getExchangeAmount(item);
    if (minAmount !== null && amount < minAmount) return false;
    if (maxAmount !== null && amount > maxAmount) return false;

    if (filters.fromCurrencies.length > 0 && !filters.fromCurrencies.includes(item.from)) {
      return false;
    }

    if (filters.toCurrencies.length > 0 && !filters.toCurrencies.includes(item.to)) {
      return false;
    }

    if (filters.statuses.length > 0) {
      const itemStatus = normalizeStatus(item.status);
      if (!filters.statuses.includes(itemStatus)) return false;
    }

    if (filters.searchQuery?.trim()) {
      const query = filters.searchQuery.trim().toLowerCase();
      const haystack = [
        item.id,
        item.date,
        item.status,
        item.from,
        item.to,
        item.amountFrom,
        item.amountTo,
        item.rate,
        `${item.from || ""} → ${item.to || ""}`,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    return true;
  });
};

export const buildExchangeFilterTags = (filters) => {
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.fromCurrencies.length > 0) {
    tags.push({
      key: "fromCurrencies",
      iconType: "currency",
      value: filters.fromCurrencies
        .map((currency) => labelById(EXCHANGE_CURRENCIES, currency))
        .join(", "),
    });
  }

  if (filters.toCurrencies.length > 0) {
    tags.push({
      key: "toCurrencies",
      iconType: "currency",
      value: filters.toCurrencies
        .map((currency) => labelById(EXCHANGE_CURRENCIES, currency))
        .join(", "),
    });
  }

  if (filters.statuses.length > 0) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((status) => labelById(getExchangeStatusOptions(), status))
        .join(", "),
    });
  }

  return tags;
};

export const removeExchangeFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "fromCurrencies":
      return { ...filters, fromCurrencies: [] };
    case "toCurrencies":
      return { ...filters, toCurrencies: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

export const isExchangeChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "from":
      return filters.fromCurrencies.length > 0;
    case "to":
      return filters.toCurrencies.length > 0;
    case "status":
      return filters.statuses.length > 0;
    case "more":
      return filters.toCurrencies.length > 0 || filters.statuses.length > 0;
    default:
      return false;
  }
};

export const exportExchangeCsv = (items) => {
  const headers = [
    "Data",
    "Moeda origem",
    "Moeda destino",
    "Valor origem",
    "Valor destino",
    "Cotação",
    "Status",
  ];

  const rows = items.map((item) => [
    item.date,
    String(item.from || "").toUpperCase(),
    String(item.to || "").toUpperCase(),
    String(item.amountFrom ?? "").replace(".", ","),
    String(item.amountTo ?? "").replace(".", ","),
    String(item.rate ?? "").replace(".", ","),
    item.status,
  ]);

  downloadCsv(
    headers,
    rows,
    `troca-de-moeda-${format(new Date(), "yyyy-MM-dd")}.csv`,
  );
};
